import { type ReactNode } from 'react';
import { cn } from '../../lib/cn';
import { RINGS, RING_ORDER, type RingId, type RingMeta } from '../../lib/game';
import { Badge } from '../primitives/Badge';
import { Tooltip } from '../primitives/Tooltip';

function pvpLabel(rule: RingMeta['pvp']): string {
  if (rule.kind === 'newbieProtection') return `Angriffe von Spielern über Level ${rule.maxAttackerLevel} sind blockiert.`;
  if (rule.kind === 'levelDifference') return `PvP frei, Ziel-Level max. ±${rule.maxDifferenceFraction * 100} % vom eigenen Niveau.`;
  if (rule.kind === 'mostlyFree') return 'PvP-Einschränkungen entfallen weitgehend.';
  return 'Kein Anfängerschutz. Volles Risiko.';
}

export interface SectorMapMarker {
  id: string;
  ring: RingId;
  /** Position around the ring, in degrees. */
  angle: number;
  kind?: 'self' | 'ally' | 'hostile' | 'neutral' | 'objective';
  label?: string;
  onSelect?: () => void;
}

export interface SectorMapProps {
  /** The ring the player currently occupies — rendered emphasised. */
  playerRing?: RingId;
  markers?: SectorMapMarker[];
  /** Highlighted on hover/selection. */
  selectedRing?: RingId | null;
  onSelectRing?: (ring: RingId) => void;
  className?: string;
}

/** Ring radii as a fraction of the viewBox. Ring 5 outermost, ring 1 the core. */
const RADII: Record<RingId, number> = { 5: 46, 4: 37, 3: 28, 2: 19, 1: 10 };

const RING_STROKE: Record<RingId, string> = {
  5: 'rgb(var(--sg-credit) / 0.45)',
  4: 'rgb(var(--sg-silicate) / 0.45)',
  3: 'rgb(var(--sg-isotope) / 0.45)',
  2: 'rgb(var(--sg-titan) / 0.45)',
  1: 'rgb(var(--sg-danger) / 0.55)',
};

const MARKER_FILL = {
  self: 'rgb(var(--sg-silicate-bright))',
  ally: 'rgb(var(--sg-credit))',
  hostile: 'rgb(var(--sg-danger))',
  neutral: 'rgb(var(--sg-obsidian-300))',
  objective: 'rgb(var(--sg-aether))',
} as const;

/**
 * The galactic ring map (GDD §3 matchmaking system).
 *
 * Five concentric zones, outer to inner: the newbie cradle at the rim, the
 * endgame core at the centre. The geometry is the mechanic — distance from the
 * centre *is* danger level — so the map is drawn to scale rather than as a list,
 * and each ring keeps its own accent colour everywhere it appears.
 */
export function SectorMap({
  playerRing,
  markers = [],
  selectedRing = null,
  onSelectRing,
  className,
}: SectorMapProps) {
  return (
    <div className={cn('relative aspect-square w-full', className)}>
      <svg viewBox="0 0 100 100" className="h-full w-full" role="img" aria-label="Sektor-Karte">
        <defs>
          <radialGradient id="sg-core-glow">
            <stop offset="0%" stopColor="rgb(244 63 63)" stopOpacity="0.35" />
            <stop offset="100%" stopColor="rgb(244 63 63)" stopOpacity="0" />
          </radialGradient>
        </defs>

        {/* Core hazard wash — the Brennpunkt reads as hot before you read a label. */}
        <circle cx="50" cy="50" r="22" fill="url(#sg-core-glow)" />

        {RING_ORDER.map((ring) => {
          const active = selectedRing === ring;
          const isPlayer = playerRing === ring;
          return (
            <g key={ring}>
              <circle
                cx="50"
                cy="50"
                r={RADII[ring]}
                fill="none"
                stroke={RING_STROKE[ring]}
                strokeWidth={isPlayer ? 1.1 : 0.5}
                strokeDasharray={isPlayer ? undefined : '2 2'}
                opacity={active || isPlayer ? 1 : 0.7}
              />
              {onSelectRing ? (
                <circle
                  cx="50"
                  cy="50"
                  r={RADII[ring]}
                  fill="none"
                  stroke="transparent"
                  strokeWidth={6}
                  className="cursor-pointer"
                  onClick={() => onSelectRing(ring)}
                >
                  <title>
                    Ring {ring} — {RINGS[ring].name}
                  </title>
                </circle>
              ) : null}
            </g>
          );
        })}

        {/* Neutron star at the centre — the Aether source and Dyson site. */}
        <circle cx="50" cy="50" r="1.8" fill="rgb(var(--sg-aether-bright))" />

        {markers.map((marker) => {
          const radius = RADII[marker.ring];
          const radians = (marker.angle * Math.PI) / 180;
          const x = 50 + radius * Math.cos(radians);
          const y = 50 + radius * Math.sin(radians);
          const kind = marker.kind ?? 'neutral';

          return (
            <circle
              key={marker.id}
              cx={x}
              cy={y}
              r={kind === 'self' ? 1.9 : 1.3}
              fill={MARKER_FILL[kind]}
              stroke={kind === 'self' ? 'rgb(var(--sg-void))' : undefined}
              strokeWidth={kind === 'self' ? 0.6 : undefined}
              className={cn(
                marker.onSelect && 'cursor-pointer',
                kind === 'hostile' && 'motion-safe:animate-pulse-glow',
              )}
              onClick={marker.onSelect}
            >
              {marker.label ? <title>{marker.label}</title> : null}
            </circle>
          );
        })}
      </svg>
    </div>
  );
}

export interface RingBadgeProps {
  ring: RingId;
  /** Adds the zone name next to the number. */
  showName?: boolean;
  className?: string;
}

/** Compact ring label with the zone's accent and PvP rules in a tooltip. */
export function RingBadge({ ring, showName = false, className }: RingBadgeProps) {
  const meta = RINGS[ring];
  return (
    <Tooltip
      side="bottom"
      content={
        <div className="space-y-0.5">
          <div className="font-display text-label uppercase text-content">
            Ring {ring} — {meta.name}
          </div>
          <div data-sg-numeric className="text-content-muted">
            Level {meta.levelRange[0]}–{meta.levelRange[1]}
          </div>
          <div className="text-content-secondary">{pvpLabel(meta.pvp)}</div>
        </div>
      }
    >
      <Badge accent={meta.accent} variant="outline" className={className}>
        Ring {ring}
        {showName ? ` · ${meta.name}` : ''}
      </Badge>
    </Tooltip>
  );
}

export interface RingLegendProps {
  playerRing?: RingId;
  onSelectRing?: (ring: RingId) => void;
  className?: string;
}

/** Legend listing all five zones with their level bands and PvP rules. */
export function RingLegend({ playerRing, onSelectRing, className }: RingLegendProps) {
  return (
    <ul className={cn('space-y-1', className)}>
      {RING_ORDER.map((ring) => {
        const meta = RINGS[ring];
        const isPlayer = playerRing === ring;
        return (
          <li key={ring}>
            <button
              type="button"
              onClick={onSelectRing ? () => onSelectRing(ring) : undefined}
              className={cn(
                'flex w-full items-center gap-2.5 rounded-control px-2 py-1.5 text-left',
                'transition-colors duration-[var(--sg-duration-fast)]',
                onSelectRing && 'hover:bg-surface-3',
                isPlayer && 'bg-surface-3',
              )}
            >
              <RingBadge ring={ring} />
              <span className="min-w-0 flex-1">
                <span className="block truncate text-xs text-content-secondary">{meta.name}</span>
                {/* The level band must never wrap — it is two short tokens and a
                    dash, and breaking it reads as a rendering fault. */}
                <span
                  data-sg-numeric
                  className="block whitespace-nowrap text-label text-content-muted"
                >
                  Level {meta.levelRange[0]}–{meta.levelRange[1]}
                </span>
              </span>
              {isPlayer ? (
                <Badge accent="silicate" size="sm">
                  Hier
                </Badge>
              ) : null}
            </button>
          </li>
        );
      })}
    </ul>
  );
}

export interface SectorMapPanelProps extends SectorMapProps {
  title?: ReactNode;
}

/** Map plus legend, the layout used by the Sektor-Karte view. */
export function SectorMapPanel({ title, playerRing, ...mapProps }: SectorMapPanelProps) {
  return (
    <div className="grid gap-4 sm:grid-cols-[1fr_15.5rem]">
      <div>
        {title ? (
          <h3 className="mb-2 font-display text-label uppercase text-content-muted">{title}</h3>
        ) : null}
        <SectorMap playerRing={playerRing} {...mapProps} />
      </div>
      <RingLegend playerRing={playerRing} onSelectRing={mapProps.onSelectRing} />
    </div>
  );
}
